<template>
    <div>
    <v-form ref="form" v-model="form.valid" class="flex flex-col justify-center items-start w-[350px] px-4 lg:px-0">
      <transition name="fade">
        <div class="w-full flex flex-col text-white items-center">
          <p class="text-sm" key="transition-source">{{$t('wizard_shared.from')}} <span v-if="extraNoPromoPercentage" class="line-through ml-1">{{ noPromoPrice }}€</span></p>
          <p class="flex items-center">
            <span class="text-2xl ml-1 font-bold"> €{{ packLowestPrice }} /</span>
          {{ $tc('shared.person',1)}}
          <span v-if="extraNoPromoPercentage" class="rounded-full bg-discount text-black text-sm px-2 py-1 ml-2">-{{ extraNoPromoPercentage }}%</span>
        </p>
        </div>
      </transition>
      <div class="my-2 w-full">
        <span class="font-bold text-white">{{$t('number_days')}}</span>
        <div class="flex justify-start items-center my-2">
            <button v-for="option in itineraries" :key="option" @click.prevent="updateSelectedItinerary(option)"
              class="rounded-xlg py-1 px-1 mr-1 font-bold h-12 w-24"
              :class="{ 'bg-white': !isItinerarySelected(option), 'bg-turquoiseBlue text-white': isItinerarySelected(option) }">
              {{ option }} {{ $t('days') }}
            </button>
        </div>
      </div>
      <div class="input-container w-full">
        <v-select
          class="js-prepend-custom-icons js-replace-arrows"
          height="50"
          v-model="origin"
          :items="originsOptions"
          prepend-inner-icon="airplane"
          :placeholder="$t('shared.where_from')"
          solo
          rounded
          single-line
          background-color="#fff"
          :disabled="form.loading || loading"
          :error="searchValues.origin === null && form.showErrors"
          item-text="label"
          item-value="code"
          :no-data-text="`${$t('shared.loading_cities')}...`"
        ></v-select>
      </div>

      <div class="input-container travellers w-full">
        <v-menu
          v-if="!isMobileMixin"
          v-model="form.showTravellersMenu"
          content-class="avm-menu avm-travellers-menu"
          attach=".input-container.travellers"
          transition="scale-transition"
          :offset-y="true"
          :close-on-content-click="false"
          :bottom="true"
          :disabled="form.loading || loading"
          class="w-full"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="travellersLabel"
              height="50"
              class="
                avm-text-field avm-text-field--travellers

                js-prepend-custom-icons js-replace-arrows
              "
              :class="{ 'dropdown-arrow-rotated': form.showTravellersMenu }"
              prepend-inner-icon="user"
              append-icon="dropdown-arrow"
              background-color="#fff"
              :placeholder="travellersLabel"
              persistent-hint
              rounded
              readonly
              solo
              v-bind="attrs"
              v-on="on"
              :disabled="form.loading || loading"
            ></v-text-field>
          </template>
          <div class="counter-container">
            <p class="counter-title">{{ ucfirst($tc('shared.adult', 2)) }}</p>
            <app-counter
              v-on:change="handleTravellersUpdate"
              field-name="adults"
              :value="travellers.adults"
              :min="cityDestinationCode === 'RAK' ? 2 : 1"
            >
            </app-counter>
          </div>
          <div v-if="showMinimumPassengerMessage" class="text-error mb-2 text-sm">
            <p>{{ $t('morocco.min_passengers_message') }}</p>
          </div>

          <div class="counter-container">
            <div>
              <p class="counter-title">{{ ucfirst($tc('shared.child', 2)) }}</p>
              <p class="hint">2-16 {{ $t('shared.years') }}</p>
            </div>
            <app-counter
              v-on:change="handleTravellersUpdate"
              field-name="childs"
              :value="travellers.childs"
            >
            </app-counter>
          </div>
          <div class="counter-container">
            <div>
              <p class="counter-title">{{ ucfirst($tc('shared.baby', 2)) }}</p>
              <p class="hint">1-2 {{ $t('shared.years') }}</p>
            </div>
            <app-counter
              v-on:change="handleTravellersUpdate"
              field-name="babies"
              :value="travellers.babies"
            >
            </app-counter>
          </div>
          <div class="flex items-center justify-end">
            <v-btn
              text
              color="secondary"
              elevation="0"
              :ripple="false"
              class="paragraph-h-16"
              @click.stop="form.showTravellersMenu = false"
            >
              {{ ucfirst($t('shared.ready')) }}
              <v-icon right size="20"> mdi-chevron-right </v-icon>
            </v-btn>
          </div>
        </v-menu>
        <v-dialog
          v-model="showDialog"
          v-if="isMobileMixin"
          fullscreen
          hide-overlay
          max-width="100vw"
          transition="dialog-bottom-transition"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="travellersLabel"
              height="50"
              class="avm-text-field avm-text-field--travellers"
              prepend-inner-icon="perm_identity"
              :placeholder="travellersLabel"
              background-color="#fff"
              persistent-hint
              rounded
              readonly
              outlined
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-card>
            <v-toolbar elevation="0">
              <v-btn icon dark @click="showDialog = false">
                <v-icon color="black">mdi-close</v-icon>
              </v-btn>
              <h3 class="heading-3">{{ $t('shared.number_of_people') }}</h3>
            </v-toolbar>
            <div class="dialog-content">
              <div class="counter-container">
                <p class="counter-title">{{ $tc('shared.adult', 2) }}</p>
                <app-counter
                  v-on:change="handleTravellersUpdate"
                  field-name="adults"
                  :value="travellers.adults"
                >
                </app-counter>
              </div>
              <div class="counter-container">
                <p class="counter-title">{{ $tc('shared.child', 2) }}</p>
                <p class="hint">2-16 {{ $t('shared.years') }}</p>
                <app-counter
                  v-on:change="handleTravellersUpdate"
                  field-name="childs"
                  :value="travellers.childs"
                >
                </app-counter>
              </div>
              <div class="counter-container">
                <p class="counter-title">{{ $tc('shared.baby', 2) }}</p>
                <p class="hint">0-2 {{ $t('shared.years') }}</p>
                <app-counter
                  v-on:change="handleTravellersUpdate"
                  field-name="babies"
                  :value="travellers.babies"
                >
                </app-counter>
              </div>
            </div>
            <v-btn
              rounded
              elevation="0"
              color="secondary"
              class="avm-btn dialog-done pl-10 -mt-1"
              width="95%"
              @click="showDialog = false"
              >{{ $t('shared.ready') }}</v-btn
            >
          </v-card>
        </v-dialog>
      </div>

      <v-btn
        class="avm-btn search-style mt-2.5 border border-solid border-grey btn-text-color"
        color="secondary"
        rounded
        block
        elevation="0"
        :loading="form.loading || loading"
        :disabled="!origin"
        @click="goToCalendar"
      >
        {{ $t('shared.search') }}
      </v-btn>
    </v-form>

    <div class="shortcut-rectangle" id="shortcut-rectangle-id">

      <div class="flex-container">

        <div class="vertical-container">
          <div class="flex-container-internal">
            <span class="days-text">
              <span>{{this.selectedItinerary}} {{$t('days')}}</span>
              <span style=" color: black; text-transform: lowercase;">{{$t('shared.from')}}</span>
            </span>
          </div>
          <span class="money-text">
            <span>{{packLowestPrice}}€</span>
            <span style=" font-size: 16px;">/{{$t('shared.person_abbr')}}</span>
          </span>
        </div>

        <v-btn
          href="#form-container-id"
          rounded
          color="#00adbc"
          class="avm-btn-reserve text-white mt-8 lg:mt-12 mr-6 mb-8 focus:outline-none w-40"
          style="color: white;"
          >{{$t('shared.reserve')}}
        </v-btn>



      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import searcherMixin from "../../mixins/searcherMixin";
import prependCustomIcons from "../../../../utils/mixins/prependCustomIcons";
import replaceArrows from "../../../../utils/mixins/replaceArrows";
import {
  SET_SEARCH_MORE_START_DATE,
  SET_SEARCH_MORE_END_DATE,
  SET_SEARCH_ORIGIN,
} from "../../../../store/search/mutation-types";
import { GET_MORE_THAN_ONE_DAY_ORIGINS_OPTIONS } from "../../../../store/destinies/getter-types";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import { ucfirst } from '../../../../utils/strings';
import { monthNames, daysOfTheWeek } from '../../../../utils/trans';

export default {
  mixins: [searcherMixin, prependCustomIcons, replaceArrows],
  components: {
    DateRangePicker,
  },
  props: {
    loading: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    cityDestinationCode: String,
    itineraries: Array,
    extraNoPromoPercentage: Number
  },
  data: () => ({
    selectingOutgoing: false,
    selectingReturn: false,
    showDialog: false,
    attrs: "",
    value: {
      startDate: "",
      endDate: "",
    },
    dateRange: {
      startDate: "2021-09-26",
      endDate: "2021-09-28",
    },
    on: "",
    today: new Date().toISOString(),
    localeData: {
      direction: "ltr",
      format: "mm/dd/yyyy",
      separator: " - ",
      applyLabel: "Apply",
      cancelLabel: "Cancel",
      weekLabel: "W",
      customRangeLabel: "Custom Range",
      daysOfWeek: [...daysOfTheWeek[window.appLocale].slice(-1), ...daysOfTheWeek[window.appLocale].slice(0, -1)],
      monthNames: monthNames[window.appLocale],
      firstDay: 1,
    },
    selectedItinerary: null,
    packLowestPrice: null,
    ucfirst,
    showMinimumPassengerMessage: false,
  }),
  computed: {
    noPromoPrice(){
      return Math.round(Number(this.packLowestPrice) / (1 - this.extraNoPromoPercentage / 100));
    },
    originsOptions() {
      return this.onDestinationsPage
        ? this.$store.state.nights.resources.origins.map(
            ({ id, code, name }) => ({
              id,
              code,
              name,
              label: name,
            })
          )
        : this.$store.getters[GET_MORE_THAN_ONE_DAY_ORIGINS_OPTIONS];
    },
    datesAreInvalid() {
      return this.form.moredaysDate.value.filter(Boolean).length !== 2;
    }
  },

  watch: {
    itineraries: {
      immediate: true,
      handler(newItineraries) {
        this.setDefaultItinerary();
      }
    },
    value: function (val) {
      const start = moment(val.startDate).format("YYYY-MM-DD");
      const end = moment(val.endDate).format("YYYY-MM-DD");
      const range = [start, end || null];
      this.form.moredaysDate.value = [...range];

      this.handleSelectMoreDate([start, end]);
    },
    $route: {
      handler(route) {
        this.form.moredaysDate.value = [route.query.start, route.query.end];
        this.$store.commit(
          SET_SEARCH_ORIGIN,
          this.originsOptions.find(({ code }) => code === route.params.origin)
        );

      },
      immediate: true,
    },
    origin(newOrigin, oldOrigin) {
      if (newOrigin !== oldOrigin) {
        this.fetchLowestPrice();
      }
    },
    selectedItinerary(newItinerary, oldItinerary) {
        if (newItinerary !== oldItinerary) {
          this.fetchLowestPrice();
        }
    }
  },

  methods: {
    setDefaultItinerary() {
      if (this.itineraries && this.itineraries.length > 0) {
        this.selectedItinerary = this.itineraries.length > 1 ? this.itineraries[1] : this.itineraries[0];
      }
    },
    toggleCalendar(open) {
      this.selectingOutgoing = open;
    },
    outgoingSelected(value) {
      const startDate = moment(value, "YYYY-MM-DD");
      const endDate = startDate.clone().add(this.selectedItinerary - 1, 'days');
      this.value = { startDate: startDate.format("YYYY-MM-DD"), endDate: endDate.format("YYYY-MM-DD") };
      this.form.moredaysDate.value = [startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD")];
      this.selectingReturn = true;
      this.selectingOutgoing = false;
      this.setSearchMoreStart(startDate);
    },
    dateFormat(classes, date) {
      if (!classes.disabled) {
        classes.disabled = date.getTime() < new Date();
      }
      return classes;
    },
    init() {
      const selectTravellers = this.$el.querySelector(
        ".avm-text-field--travellers .v-input__slot"
      );
      if (selectTravellers) {
        selectTravellers.addEventListener("click", () => {
          this.showDialog = true;
          this.form.showTravellersMenu = true;
        });
      }
      const selectMoreDates = this.$el.querySelector(
        ".avm-text-field--moredates .v-input__slot"
      );
      if (selectMoreDates) {
        selectMoreDates.addEventListener("click", () => {
          this.form.moredaysDate.showMenu = true;
        });
      } else {
        this.form.moredaysDate.showMenu = false;
      }
    },
    allowedDates: (val) => new Date(val) >= new Date(),
    disabledDates: (val) => new Date(vale) <= new Date(),
    updateSelectedItinerary(index) {
      this.selectedItinerary = index;
    },
    isItinerarySelected(index) {
      return index === this.selectedItinerary;
    },
    goToCalendar() {
      let destination = this.cityDestinationCode;
      const adults = this.travellers.adults;
      const children = this.travellers.childs;
      const babies = this.travellers.babies;
      return router.push(`viaje/calendario/${this.origin.code}/${destination}/pack?adults=${adults}&children=${children}&babies=${babies}&itinerary=${this.selectedItinerary}`);
    },
    fetchLowestPrice() {
      const url = `/api/lowest-price/${this.cityDestinationCode}?origin=${this.origin.code}&itinerary=${this.selectedItinerary}`;

      axios.get(url)
        .then(({data}) => this.packLowestPrice = data.pack_lowest_price)
        .catch(error => {
          this.packLowestPrice = '1100';
          console.error("There was an error fetching the lowest price: ", error);
        });
    },

    setDefaultItinerary() {
    if (this.itineraries && this.itineraries.length > 0) {
      this.selectedItinerary = this.itineraries.length > 1 ? this.itineraries[1] : this.itineraries[0];
    }
  },

  },

  mounted() {

    this.init();

    this.$store.commit(SET_SEARCH_ORIGIN, {code: 'MAD'});

    this.setDefaultItinerary();

    this.handleScroll = function() {
    var elementToFade = document.getElementById('shortcut-rectangle-id');
    var scrollPosition = window.scrollY;

    var fadeThreshold = 5500;

    if (scrollPosition > fadeThreshold) {
      elementToFade.classList.add('fade-out');
    } else {
      elementToFade.classList.remove('fade-out');
    }
  };

  window.addEventListener('scroll', this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
}

};

</script>

<style lang="scss">

</style>


<style lang="scss" scoped>
.date-more-days {
  max-width: 285px !important;
}

.search-form::v-deep {
  .v-date-picker-table {
    height: auto;
  }
}
.max-width-date {
  @media(max-width: 992px){
    max-width: 100%;
  }
}
.input-container{
  height: 60px;
}

.v-input{
  position: relative;
  &::after{
    content: "";
    width: 100%;
    height: 50px;
    top: 0;
    left: 0;
    border-radius: 28px;
    position: absolute;
    border: 2px solid black;
    display: none;
  }

  &.rounded-select-selected{
    &:after{
      display: block;
    }
  }
}
.theme--light.v-btn.v-btn--disabled, .theme--light.v-btn.v-btn--disabled .v-btn__loading, .theme--light.v-btn.v-btn--disabled .v-icon{
  color: white !important;
}

    @media (min-width: 768px) {

        .shortcut-rectangle{
            display:none;
        }
        .booking-floating-btn{
            display:none;
        }
    }
        .shortcut-rectangle{
            z-index: 1;
            position: fixed;
            width: 100%;
            height: 80px;
            bottom: 0px;
            left: 0;
            background-color: white;
            margin: 852px 0 0;
            box-shadow: 0 -2px 10px 0 rgba(0, 0, 0, 0.25);

        }

        .avm-btn-reserve{
          text-transform: capitalize;
          font-family: Avenir;
          font-size: 18px;
          font-weight: 100;
          line-height: 1;
          height: 52px !important;
        }

        .flex-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 100%;
        }

        .days-text{
          display: inline-block;
          white-space: nowrap;
          width: 104px;
          height: 14px;
          margin: 4px 50px 0px 16px;
          font-family: Avenir;
          font-size: 16px;
          font-weight: 900;
          font-stretch: normal;
          font-style: normal;
          line-height: 1;
          letter-spacing: normal;
          text-align: left;
          color: #2fa1ad;
      }

      .flex-container-internal{
        display: flex !important;
        align-items: center !important;
        gap: 10px !important;
      }

      .money-text{
        flex-grow: 0;
        font-family: Avenir;
        font-size: 22px;
        font-weight: 900;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        margin: 4px 50px 8px 16px;
        letter-spacing: normal;
        text-align: left;
        color: #000;
      }

      .from-text{
        margin-left:5px;
        font-family: Avenir;
        font-size: 14px;
        font-weight: 900;
        font-stretch: normal;
        font-style: normal;
        line-height: 1;
        letter-spacing: normal;
        text-align: left;
        color: #000000;
      }

      .vertical-container {
        display: flex;
        flex-direction: column;
      }

      .reserve-button-text{
        color: white;

      }

      .fade-out {
        opacity: 0;
        transition: opacity 0.5s ease-out;
      }

      .v-select__selection--comma {
        color: rgba(0,0,0,.87);
      }
</style>
